import useSWR from 'swr';
import { useMemo, useCallback } from 'react';

import { useRouter } from 'src/routes/hooks';

import { useAppContext } from 'src/context/app';
import { useAuthContext } from 'src/auth/hooks';
import axiosInstance, { fetcher, endpoints } from 'src/lib/axios';

// ----------------------------------------------------------------------

export type NotifieringsFilter = 'alla' | 'olasta' | 'arkiverade';

export type Notifiering = {
  id: string;
  /**
   * Föreningen notifieringen gäller. Listan spänner över alla användarens föreningar — en
   * notifiering skrivs där källan hör hemma, inte där mottagaren råkar stå i menyn — så raden
   * måste bära med sig sin förening för att kunna etiketteras och länkas rätt.
   */
  orgId: string;
  /** Null om användaren lämnat föreningen sedan notifieringen skapades. */
  orgNamn: string | null;
  /** Konstantnamnet i backends NotifieringsTyp, t.ex. NYTT_BREVLÅDEMEDDELANDE. */
  typ: string;
  domän: string;
  domänEtikett: string;
  /** Iconify-namn, valt i backend så att nya typer inte kräver någon ändring här. */
  ikon: string;
  titel: string;
  beskrivning: string | null;
  länk: string | null;
  läst: boolean;
  arkiverad: boolean;
  skapadVid: string;
};

export type NotifieringarSvar = {
  notifieringar: Notifiering[];
  antalAlla: number;
  antalOlästa: number;
  antalArkiverade: number;
};

export type NotifieringsInställning = {
  domän: string;
  etikett: string;
  påslagen: boolean;
};

// ----------------------------------------------------------------------

const TOMT: NotifieringarSvar = {
  notifieringar: [],
  antalAlla: 0,
  antalOlästa: 0,
  antalArkiverade: 0,
};

/**
 * Till skillnad från övriga actions används inte det delade swrOptions här — det stänger av all
 * revalidering, och en notifieringsklocka som bara uppdateras vid omladdning är ingen klocka.
 */
const swrOptions = {
  refreshInterval: 60_000,
  revalidateOnFocus: true,
  keepPreviousData: true,
};

export function useNotifieringar(filter: NotifieringsFilter = 'alla', limit = 50) {
  const url = `${endpoints.notifieringar.lista}?filter=${filter}&limit=${limit}`;

  const { data, isLoading, error, isValidating, mutate } = useSWR<NotifieringarSvar>(
    url,
    fetcher,
    swrOptions
  );

  const svar = data ?? TOMT;

  /** Markerar läst och hämtar om — antalen i fliklisten kommer från servern. */
  const markeraLäst = useCallback(
    async (id: string) => {
      await axiosInstance.post(endpoints.notifieringar.läs(id));
      await mutate();
    },
    [mutate]
  );

  const markeraAllaLästa = useCallback(async () => {
    await axiosInstance.post(endpoints.notifieringar.läsAlla);
    await mutate();
  }, [mutate]);

  const arkivera = useCallback(
    async (id: string) => {
      await axiosInstance.post(endpoints.notifieringar.arkivera(id));
      await mutate();
    },
    [mutate]
  );

  return useMemo(
    () => ({
      notifieringar: svar.notifieringar,
      antalAlla: svar.antalAlla,
      antalOlästa: svar.antalOlästa,
      antalArkiverade: svar.antalArkiverade,
      notifieringarLoading: isLoading,
      notifieringarError: error,
      notifieringarValidating: isValidating,
      notifieringarEmpty: !isLoading && svar.notifieringar.length === 0,
      markeraLäst,
      markeraAllaLästa,
      arkivera,
      uppdatera: mutate,
    }),
    [svar, isLoading, error, isValidating, markeraLäst, markeraAllaLästa, arkivera, mutate]
  );
}

// ----------------------------------------------------------------------

/**
 * Öppnar en notifiering: markerar den läst och går till det den handlar om.
 *
 * <p>Listan spänner över alla användarens föreningar, så länken kan peka in i en annan
 * förening än den som är vald i menyn. Den valda föreningen byts därför först — annars leder
 * "/fastighet/mina-hem" till rätt sida i fel förening, vilket ser ut som att notifieringen
 * ljög. Delas av lådan och sidan så att de inte kan glida isär.
 */
export function useNotifieringsÖppnare(markeraLäst: (id: string) => Promise<void>) {
  const router = useRouter();
  const { organisationer } = useAuthContext();
  const { organisation, uppdateraValdOrganisation } = useAppContext();

  return useCallback(
    async (notifiering: Notifiering) => {
      if (!notifiering.läst) {
        await markeraLäst(notifiering.id);
      }

      if (notifiering.orgId && notifiering.orgId !== organisation?.id) {
        // Bara föreningar användaren faktiskt har kvar. Saknas den lämnas valet orört och
        // länken följs ändå — sidan får då visa det den kan.
        const mål = (organisationer ?? []).find((org) => org.id === notifiering.orgId);
        if (mål) {
          uppdateraValdOrganisation(mål);
        }
      }

      if (notifiering.länk) {
        router.push(notifiering.länk);
      }
    },
    [markeraLäst, organisation?.id, organisationer, router, uppdateraValdOrganisation]
  );
}

// ----------------------------------------------------------------------

export function useNotifieringsInställningar(aktiv: boolean) {
  const { data, isLoading, mutate } = useSWR<NotifieringsInställning[]>(
    aktiv ? endpoints.notifieringar.inställningar : null,
    fetcher,
    { revalidateOnFocus: false }
  );

  const växla = useCallback(
    async (domän: string, påslagen: boolean) => {
      await axiosInstance.put(endpoints.notifieringar.inställningar, { domän, påslagen });
      await mutate();
    },
    [mutate]
  );

  return useMemo(
    () => ({
      inställningar: data ?? [],
      inställningarLoading: isLoading,
      växla,
    }),
    [data, isLoading, växla]
  );
}
